"use client"

import { useEffect, useRef, useState } from "react"
import { EditorContent, EditorContext, useEditor } from "@tiptap/react"

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit"
import { Image } from "@tiptap/extension-image"
import { TextAlign } from "@tiptap/extension-text-align"
import { Typography } from "@tiptap/extension-typography"
import { Selection } from "@tiptap/extensions"

// --- UI Primitives ---
import { Button } from "components/tiptap-ui-primitive/button"
import { Spacer } from "components/tiptap-ui-primitive/spacer"
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "components/tiptap-ui-primitive/toolbar"

// --- Tiptap Node ---
import { ImageUploadNode } from "components/tiptap-node/image-upload-node/image-upload-node-extension"
import { HorizontalRule } from "components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension"
import "components/tiptap-node/blockquote-node/blockquote-node.scss"
import "components/tiptap-node/code-block-node/code-block-node.scss"
import "components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss"
import "components/tiptap-node/list-node/list-node.scss"
// import "components/tiptap-node/image-node/image-node.scss"
import "components/tiptap-node/image-carousel-node/CarouselComponent.scss"
import "components/tiptap-node/heading-node/heading-node.scss"
import "components/tiptap-node/paragraph-node/paragraph-node.scss"

// --- Tiptap UI ---
import { ImageUploadButton } from "components/tiptap-ui/image-upload-button"
import { ListDropdownMenu } from "components/tiptap-ui/list-dropdown-menu"
import {
  ColorHighlightPopoverContent,
} from "components/tiptap-ui/color-highlight-popover"
import {
  LinkPopover,
  LinkContent,
  LinkButton,
} from "components/tiptap-ui/link-popover"
import {MarkButton, markIcons} from "components/tiptap-ui/mark-button"
import {TextAlignButton, textAlignIcons} from "components/tiptap-ui/text-align-button"
import { UndoRedoButton } from "components/tiptap-ui/undo-redo-button"

// --- Icons ---
import { ArrowLeftIcon } from "components/tiptap-icons/arrow-left-icon"
import { HighlighterIcon } from "components/tiptap-icons/highlighter-icon"
import { LinkIcon } from "components/tiptap-icons/link-icon"

// --- Hooks ---
import { useIsBreakpoint } from "hooks/use-is-breakpoint"
import { useWindowSize } from "hooks/use-window-size"
import { useCursorVisibility } from "hooks/use-cursor-visibility"

// --- Lib ---
import { MAX_FILE_SIZE } from "lib/tiptap-utils"

// --- Styles ---
import "components/tiptap-templates/simple/simple-editor.scss"

import CollapsableToolbarGroup from "../../tiptap-ui/collapsable-toolbar-group/collapsable-toolbar-group";
import ImageCarousel from "../../tiptap-node/image-carousel-node/image-carousel-node";
import {ImageCarouselButton} from "../../tiptap-ui/image-carousel-button";

const MainToolbarContent = ({
  onLinkClick,
  isMobile
}) => {
  return (
    <>
      <Spacer />
      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <ListDropdownMenu types={["bulletList", "orderedList"]} portal={isMobile} />
      </ToolbarGroup>
      <ToolbarSeparator />
      <CollapsableToolbarGroup collapsedMaxWidth={1000} Icon={markIcons["bold"]}>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="underline" />
      </CollapsableToolbarGroup>
      <ToolbarSeparator />
      <CollapsableToolbarGroup collapsedMaxWidth={1200} Icon={textAlignIcons["left"]}>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </CollapsableToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
          {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      <ImageCarouselButton />
      </ToolbarGroup>
      <Spacer />
    </>
  );
}

const MobileToolbarContent = ({
  type,
  onBack
}) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? (
      <ColorHighlightPopoverContent />
    ) : (
      <LinkContent />
    )}
  </>
)

export function SimpleEditor({
  editable,
  handleImageUpload,
  onEditorCreate,
  content = null,
                             }) {
  const isMobile = useIsBreakpoint()
  const { height } = useWindowSize()
  const [mobileView, setMobileView] = useState("main")
  const toolbarRef = useRef(null)

  const editor = useEditor({
    editable: editable,
    immediatelyRender: false,
    onCreate: ({ editor }) => {
      onEditorCreate?.(editor)
    },
    content: content,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
        class: "simple-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      HorizontalRule,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      // Image,
      Typography,
      Selection,
      ImageCarousel.configure({
        upload: handleImageUpload,
      }),
    ],
  });

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  })

  useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main")
    }
  }, [isMobile, mobileView])

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editable, editor]);

  return (
    <div className="simple-editor-wrapper">
      <EditorContext.Provider value={{ editor }}>
        {editable && (
          <Toolbar
            ref={toolbarRef}
            style={{
              ...(isMobile
                ? {
                  position: "fixed",
                  top: "auto", 
                  bottom: 0 
                }
                : {}),
            }}>
            {mobileView === "main" ? (
              <MainToolbarContent
                onHighlighterClick={() => setMobileView("highlighter")}
                onLinkClick={() => setMobileView("link")}
                isMobile={isMobile} />
            ) : (
              <MobileToolbarContent
                type={mobileView === "highlighter" ? "highlighter" : "link"}
                onBack={() => setMobileView("main")} />
            )}
          </Toolbar>
        )}

        <EditorContent editor={editor} role="presentation" className="simple-editor-content" />
      </EditorContext.Provider>
    </div>
  );
}
